import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { announcementApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

/**
 * Platform announcements: a banner under the top bar for everyone in an
 * audience while it is live, optionally sent once as a notification too.
 *
 * Switching one off takes the banner down and keeps the record; deleting
 * removes it. Neither takes back a notification that already went out.
 */

const TABS = [
  { key: 'on', label: 'Switched on', is_active: true },
  { key: 'off', label: 'Switched off', is_active: false },
  { key: 'all', label: 'All', is_active: undefined },
];

const AUDIENCES = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'STUDENTS', label: 'Students' },
  { value: 'COMPANIES', label: 'Companies' },
];

const AUDIENCE_LABELS = Object.fromEntries(AUDIENCES.map((row) => [row.value, row.label]));

const PAGE_SIZE = 20;

const EMPTY = {
  title: '',
  body: '',
  link: '',
  audience: 'ALL',
  starts_at: '',
  ends_at: '',
  is_active: true,
  notify: false,
};

/** An ISO timestamp as a `datetime-local` value, in the browser's time zone. */
function toLocalInput(value) {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function fromLocalInput(value) {
  return value ? new Date(value).toISOString() : null;
}

function formatWhen(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

/** Live, scheduled, ended or off -- one badge. */
function stateOf(row) {
  if (!row.is_active) return { tone: 'neutral', label: 'Off' };
  if (row.is_live) return { tone: 'success', label: 'Live' };
  if (new Date(row.starts_at) > new Date()) return { tone: 'info', label: 'Scheduled' };
  return { tone: 'neutral', label: 'Ended' };
}

/** The bell tile's colour: green while it shows, violet while it waits. */
const AVATAR_TONES = { Live: 'success', Scheduled: 'brand' };

export function AdminAnnouncementsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState('on');
  const [page, setPage] = useState(1);
  // `null` closed, `'new'` creating, or the row being edited.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleting, setDeleting] = useState(null);
  const [switchingOff, setSwitchingOff] = useState(null);

  const activeFilter = TABS.find((row) => row.key === tab)?.is_active;
  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(activeFilter === undefined ? {} : { is_active: activeFilter }),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-announcements', params],
    queryFn: () => announcementApi.listAnnouncements(params),
    placeholderData: keepPreviousData,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
    // The admin's own banner shows "Everyone" announcements too.
    void queryClient.invalidateQueries({ queryKey: ['announcements'] });
  }

  function openNew() {
    setForm(EMPTY);
    setEditing('new');
  }

  function openEdit(row) {
    setForm({
      title: row.title,
      body: row.body,
      link: row.link ?? '',
      audience: row.audience,
      starts_at: toLocalInput(row.starts_at),
      ends_at: toLocalInput(row.ends_at),
      is_active: row.is_active,
      notify: false,
    });
    setEditing(row);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: form.title.trim(),
        body: form.body.trim(),
        link: form.link.trim() || null,
        audience: form.audience,
        ends_at: fromLocalInput(form.ends_at),
        is_active: form.is_active,
        notify: form.notify,
      };
      if (form.starts_at) body.starts_at = fromLocalInput(form.starts_at);
      return editing === 'new'
        ? announcementApi.createAnnouncement(body)
        : announcementApi.updateAnnouncement(editing.id, body);
    },
    onSuccess: (saved) => {
      toast.success(
        form.notify && saved.notified_at ? 'Saved — notifications are on their way' : 'Saved',
      );
      setEditing(null);
      invalidate();
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save the announcement')),
  });

  const setActive = useMutation({
    mutationFn: ({ id, is_active }) => announcementApi.updateAnnouncement(id, { is_active }),
    onSuccess: (saved) => {
      toast.success(saved.is_active ? 'Switched on' : 'Switched off');
      invalidate();
    },
    onError: (updateError) =>
      toast.error(errorMessage(updateError, 'Could not update the announcement')),
  });

  const remove = useMutation({
    mutationFn: (id) => announcementApi.deleteAnnouncement(id),
    onSuccess: () => {
      toast.success('Announcement deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (deleteError) =>
      toast.error(errorMessage(deleteError, 'Could not delete the announcement')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const alreadySent = editing && editing !== 'new' && Boolean(editing.notified_at);
  // Past times cannot be picked. An announcement that already started keeps
  // its own start (it may not be moved earlier); everything else starts now.
  const now = toLocalInput(new Date());
  const savedStart = editing && editing !== 'new' ? toLocalInput(editing.starts_at) : '';
  const startMin = savedStart && savedStart < now ? savedStart : now;
  const endMin = form.starts_at && form.starts_at > now ? form.starts_at : now;
  const startError =
    form.starts_at && form.starts_at < startMin ? 'Pick a time from now on.' : undefined;
  const savedEnd = editing && editing !== 'new' ? toLocalInput(editing.ends_at) : '';
  // An end left as it was saved is not being set, even if it has passed.
  const endError = !form.ends_at
    ? undefined
    : form.ends_at < now && form.ends_at !== savedEnd
      ? 'Pick a time from now on.'
      : form.starts_at && form.ends_at <= form.starts_at
        ? 'The end must be after the start.'
        : undefined;
  const ready =
    form.title.trim().length >= 3 && form.body.trim().length >= 3 && !startError && !endError;

  return (
    <div>
      <PageHeader
        title="Announcements"
        description="A banner under the top bar for everyone in the audience while it is live. It can also go out once as a notification."
        action={
          <Button onClick={openNew}>
            <Icon name="plus" size={16} />
            New announcement
          </Button>
        }
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS.map((row) => ({ key: row.key, label: row.label }))}
            value={tab}
            onChange={(key) => {
              setTab(key);
              setPage(1);
            }}
            label="Announcement status"
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading announcements…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="bell" size={22} />}
              title="No announcements"
              description={
                tab === 'off'
                  ? 'Nothing is switched off.'
                  : 'Write one and it shows as a banner for its audience.'
              }
              action={tab !== 'off' && <Button onClick={openNew}>New announcement</Button>}
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => {
            const state = stateOf(row);
            return (
              <BoxRow
                key={row.id}
                accent={state.tone}
                leading={<BoxAvatar icon="bell" tone={AVATAR_TONES[state.label] ?? 'neutral'} />}
                title={row.title}
                badges={
                  <>
                    <Badge dot tone={state.tone}>
                      {state.label}
                    </Badge>
                    {row.notified_at && <Badge tone="brand">Notified</Badge>}
                  </>
                }
                facts={[
                  { icon: 'users', text: AUDIENCE_LABELS[row.audience] },
                  { icon: 'calendar', text: `From ${formatWhen(row.starts_at)}` },
                  {
                    icon: 'calendarClock',
                    text: row.ends_at ? `Until ${formatWhen(row.ends_at)}` : 'No end date',
                  },
                ]}
                description={row.body}
                meta={
                  <span className="text-ink-muted text-xs">
                    Last update {formatDate(row.updated_at)}
                  </span>
                }
                actions={
                  <CardActions>
                    {row.is_active ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        title={`Switch off ${row.title}`}
                        onClick={() => setSwitchingOff(row)}
                      >
                        <Icon name="power" size={14} />
                        Switch off
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        title={`Switch on ${row.title}`}
                        isLoading={setActive.isPending && setActive.variables?.id === row.id}
                        onClick={() => setActive.mutate({ id: row.id, is_active: true })}
                      >
                        {!(setActive.isPending && setActive.variables?.id === row.id) && (
                          <Icon name="restore" size={14} />
                        )}
                        Switch on
                      </Button>
                    )}
                    <Button
                      variant="tintBrand"
                      size="sm"
                      title={`Edit ${row.title}`}
                      onClick={() => openEdit(row)}
                    >
                      <Icon name="pencil" size={14} />
                      Edit
                    </Button>
                  </CardActions>
                }
                corner={
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    title={`Delete ${row.title}`}
                    onClick={() => setDeleting(row)}
                  >
                    <Icon name="trash" size={14} />
                    Delete
                  </Button>
                }
              />
            );
          })}
        </BoxList>
      </ListCard>

      <ConfirmDialog
        open={switchingOff !== null}
        title="Switch this announcement off?"
        message="The banner comes down for everyone. The announcement is kept and you can switch it back on."
        confirmLabel="Switch off"
        isLoading={setActive.isPending}
        onConfirm={() =>
          setActive.mutate(
            { id: switchingOff.id, is_active: false },
            { onSuccess: () => setSwitchingOff(null) },
          )
        }
        onCancel={() => setSwitchingOff(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete “${deleting?.title ?? ''}”?`}
        message="The banner comes down and the announcement is removed. Notifications already sent stay in people's feeds."
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />

      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'New announcement' : 'Edit announcement'}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button disabled={!ready} isLoading={save.isPending} onClick={() => save.mutate()}>
              {form.notify ? 'Save and notify' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Title"
            required
            maxLength={150}
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
          <Textarea
            label="Message"
            required
            rows={3}
            maxLength={2000}
            value={form.body}
            onChange={(event) => setForm({ ...form, body: event.target.value })}
          />
          <Input
            label="Link"
            hint="Optional “Learn more”. A page in the app (/internships) or a full web address."
            maxLength={500}
            value={form.link}
            onChange={(event) => setForm({ ...form, link: event.target.value })}
          />
          <Select
            label="Audience"
            value={form.audience}
            onChange={(event) => setForm({ ...form, audience: event.target.value })}
          >
            {AUDIENCES.map((row) => (
              <option key={row.value} value={row.value}>
                {row.label}
              </option>
            ))}
          </Select>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Starts"
              type="datetime-local"
              hint="Empty means now."
              min={startMin}
              error={startError}
              value={form.starts_at}
              onChange={(event) => setForm({ ...form, starts_at: event.target.value })}
            />
            <Input
              label="Ends"
              type="datetime-local"
              hint="Empty means until switched off."
              min={endMin}
              error={endError}
              value={form.ends_at}
              onChange={(event) => setForm({ ...form, ends_at: event.target.value })}
            />
          </div>
          <Checkbox
            label="Switched on"
            checked={form.is_active}
            onChange={(event) =>
              setForm({
                ...form,
                is_active: event.target.checked,
                // Only a switched-on announcement can be sent.
                notify: event.target.checked && form.notify,
              })
            }
          />
          {alreadySent ? (
            <p className="text-ink-muted text-sm">
              Sent as a notification on {formatWhen(editing.notified_at)}. It is not sent again.
            </p>
          ) : (
            <>
              <Checkbox
                label="Also send as a notification"
                checked={form.notify}
                disabled={!form.is_active}
                onChange={(event) => setForm({ ...form, notify: event.target.checked })}
              />
              {form.notify && (
                <Alert tone="warning">
                  Everyone in the audience gets a notification, and an email unless they turned
                  account emails off. This happens once and cannot be undone.
                </Alert>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
